import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";

export const BannerContext = React.createContext({});

const MAX_STACK = 8;
const DANGER_DEDUPE_MS = 8000;

function dismissMsForType(alertType) {
  if (alertType === 'success') return 2000;
  if (alertType === 'danger' || alertType === 'error') return 9000;
  if (alertType === 'warning') return 7000;
  return 5000;
}

export const BannerContextProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const timeoutsRef = useRef(new Map());
  const lastDangerBannerRef = useRef({ text: '', at: 0 });

  const clearTimeoutFor = useCallback((id) => {
    const t = timeoutsRef.current.get(id);
    if (t) {
      clearTimeout(t);
      timeoutsRef.current.delete(id);
    }
  }, []);

  const dismissNotification = useCallback((id) => {
    clearTimeoutFor(id);
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, [clearTimeoutFor]);

  const clearBanner = useCallback(() => {
    timeoutsRef.current.forEach((t) => clearTimeout(t));
    timeoutsRef.current.clear();
    setNotifications([]);
  }, []);

  const showMessage = useCallback((text, alertType = 'info', autoDismiss = true, mode = 'notification', onClick = null) => {
    // All in-app toasts use bottom-right Notification; 'banner' was legacy top-bar — treat the same.
    void mode;

    const displayText = text == null ? '' : String(text);
    if (!displayText) return;

    // Avoid double identical danger toasts in quick succession (e.g. stream error fan-out).
    if (alertType === 'danger' || alertType === 'error') {
      const now = Date.now();
      const last = lastDangerBannerRef.current;
      if (last.text === displayText && now - last.at < DANGER_DEDUPE_MS) {
        return;
      }
      lastDangerBannerRef.current = { text: displayText, at: now };
    }

    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const item = {
      id,
      text: displayText,
      type: alertType,
      autoDismiss: !!autoDismiss,
      onClick: onClick || null,
    };

    setNotifications((prev) => {
      const next = [...prev, item];
      // Drop oldest if over cap; clear their timers
      if (next.length > MAX_STACK) {
        const dropped = next.slice(0, next.length - MAX_STACK);
        dropped.forEach((n) => clearTimeoutFor(n.id));
        return next.slice(-MAX_STACK);
      }
      return next;
    });

    if (autoDismiss) {
      const t = setTimeout(() => {
        dismissNotification(id);
      }, dismissMsForType(alertType));
      timeoutsRef.current.set(id, t);
    }
  }, [clearTimeoutFor, dismissNotification]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach((t) => clearTimeout(t));
      timeoutsRef.current.clear();
    };
  }, []);

  // Backward-compatible single-message fields (most recent toast)
  const latest = notifications.length ? notifications[notifications.length - 1] : null;
  const bannerMessage = latest?.text ?? null;
  const bannerType = latest?.type ?? 'info';
  const bannerOnClick = latest?.onClick ?? null;
  const displayMode = 'notification';

  const value = useMemo(() => ({
    notifications,
    bannerMessage,
    bannerType,
    bannerOnClick,
    displayMode,
    showMessage,
    clearBanner,
    dismissNotification,
  }), [
    notifications,
    bannerMessage,
    bannerType,
    bannerOnClick,
    showMessage,
    clearBanner,
    dismissNotification,
  ]);

  return (
    <BannerContext.Provider value={value}>
      {children}
    </BannerContext.Provider>
  );
};
